import { gaMeasurementId, mixPanelToken, mixpanelCaptureEnable } from "common/config";
import mixpanel, { Dict } from "mixpanel-browser";

export type MixpanelCaptureEntry = {
  mixpanelKey: string;
  route: string;
  pageName: string;
  eventName: string;
  data: Dict;
};
import ReactGA from "react-ga4";
import gaTrackingDefinitions from "../../../../packages/common/config/gaTrackingDefinations.json"; // Import the JSON
import mixpanelTrackingDefinitions from "../../../../packages/common/config/mixpanelTrackingDefinations.json"; // Import the JSON
import { Device } from "@capacitor/device";

ReactGA.initialize([
  {
    trackingId: gaMeasurementId,
  },
]);

mixpanel.init(mixPanelToken);

class AnalyticsService {
  static trackGAPagesHandler(pagePath) {
    ReactGA.ga("set", "page", pagePath);
    ReactGA.ga("send", "pageview");
  }

  static trackGAEventHandler_orig(eventCategory, eventAction, eventLabel) {
    const eventData = {
      eventCategory: eventCategory,
      eventAction: eventAction,
      eventLabel: eventLabel,
    };
    ReactGA.ga("send", "event", eventData);
  }

  static trackGAEventHandler(keyName) {
    const eventData = gaTrackingDefinitions.gaEvents[keyName];
    if (!eventData) {
      console.warn(`GA event definition for keyName"${keyName}" not found`);
      return;
    }
    ReactGA.ga("send", "event", {
      eventCategory: eventData.eventCategory,
      eventAction: eventData.eventAction,
      eventLabel: eventData.eventLabel,
    });
  }

  //////////////////////////
  // Mixpanel related
  //////////////////////////
  static trackMx_Orig(event: string, data: Dict) {
    mixpanel.track(event, data);
  }

  static trackMx(keyName, data: Dict) {
    const eventData = mixpanelTrackingDefinitions.mixpanelEvents[keyName];
    if (!eventData) {
      console.warn(
        `Mixpanel event definition for keyName"${keyName}" not found`,
      );
      return;
    }

    // Combine eventData and data, giving precedence to the properties in data
    const payload = {
      PageName: eventData.PageName,
      EventDetail: eventData.EventDetail,
      ...data,
    };
    mixpanel.track(eventData.EventName, payload);

    // Phase 2: capture for Notion when enabled via VITE_MIXPANEL_CAPTURE_ENABLE (local/dev: true, production: false)
    if (mixpanelCaptureEnable && typeof window !== "undefined") {
      const win = window as Window & { __mixpanelCapture?: MixpanelCaptureEntry[] };
      if (!Array.isArray(win.__mixpanelCapture)) {
        win.__mixpanelCapture = [];
      }
      win.__mixpanelCapture.push({
        mixpanelKey: keyName,
        route: window.location.pathname,
        pageName: eventData.PageName,
        eventName: eventData.EventName,
        data: payload,
      });
    }
  }

  /**
   * Alias mixpanel.
   *
   * @param {string} userToken
   * @memberof MixpanelService
   */
  static alias(userToken: string): void {
    mixpanel.alias(userToken);
  }

  /**
   * Identify mixpanel.
   *
   * @param {string} userToken
   * @memberof MixpanelService
   */
  static identify(userToken: string): void {
    mixpanel.identify(userToken);
  }

  /**
 * Push Super Properties
 * @param properties 
 * @memberof MixpanelService
 * 
 * Example
 mixpanel.register({"age": 28,"gender":"male","source":"facebook"
 }); 
 * 
 */
  static register(properties: Dict): void {
    mixpanel.register(properties);
  }

  /**
   * For new registered users
   * @param properties
   */
  static setPeople(properties: Dict): void {
    mixpanel.people.set(properties);
  }

  /**
   * Clears super properties and generates a new random distinct_id for this instance.
   * Useful for clearing data when a user logs out.
   */
  static reset() {
    mixpanel.reset();
  }

  static async setUserProfileProps(
    userData: Dict,
    appendOverridePeople: Dict = null,
    appendOverrideSuperProps = null,
  ) {
    // const generatedUuid = uuidv4(); // Generate a new UUID
    // const platform = navigator.platform; // Platform (OS)
    // const userAgent = navigator.userAgent; // User agent string

    // // Example of how you could extract some basic information
    // const deviceInfo =`${platform}-${userAgent}`;

    // const deviceUUID = generatedUuid;

    const userDeviceInfo = await Device.getInfo();
    const uuid = await Device.getId();

    let deviceInfo = "";
    let deviceUUID = "";

    if (userDeviceInfo) {
      deviceInfo =
        userDeviceInfo.platform +
        "-" +
        userDeviceInfo.model +
        "-" +
        userDeviceInfo.operatingSystem;
    }

    deviceUUID = `{"identifier":"${uuid?.identifier}"}`;

    console.log("deviceUUID", deviceUUID);
    console.log("deviceInfo", deviceInfo);

    let accessibilityNeed = "";
    let gender = "";
    let yearOfBirth = "";

    if (userData) {
      accessibilityNeed = userData?.details?.accessibility_need
        ? userData.details.accessibility_need
        : userData?.accessibility_need;
      gender = userData?.details?.gender
        ? userData.details.gender
        : userData?.gender;
      yearOfBirth = userData?.details?.year_of_birth
        ? userData.details.year_of_birth
        : userData?.year_of_birth;
    }

    let mixPanelDataForPeople: any = {
      AccountId: userData.id,
      AccessNeed: userData.ability,
      AccessibilityNeed: accessibilityNeed,
      EmailConfirmed: true,
      $email: userData.email,
      Gender: gender,
      $name: userData.name,
      YearOfBirth: yearOfBirth,
      UUID: deviceUUID,
      DeviceInfo: deviceInfo,
      UserLevel: userData.level,
    };

    if (userData?.roles) {
      mixPanelDataForPeople.isSociabilityMapper =
        userData?.roles?.isSociabilityMapper == true ? "Yes" : "No";
      mixPanelDataForPeople.isSociabilityTeam =
        userData?.roles?.isSociabilityTeam == true ? "Yes" : "No";
    }

    // If you need to register a new super property add it to this object
    let mixPanelDataForSuperProperties = { ...mixPanelDataForPeople };
    // Deleting the props previously we did not include in the super properties
    // delete mixPanelDataForSuperProperties.DeviceInfo;

    if (appendOverridePeople) {
      mixPanelDataForPeople = {
        ...mixPanelDataForPeople,
        ...appendOverridePeople,
      };
    }

    if (appendOverrideSuperProps) {
      mixPanelDataForSuperProperties = {
        ...mixPanelDataForSuperProperties,
        ...appendOverrideSuperProps,
      };
    }

    console.log("mixPanelDataForPeople", mixPanelDataForPeople);
    console.log(
      "mixPanelDataForSuperProperties",
      mixPanelDataForSuperProperties,
    );

    //create profile at MixPanel
    this.setPeople(mixPanelDataForPeople);

    // console.log('profile page onProfileAccessibilitySubmit mixpanel.identify', parsedData.id);
    this.identify(userData.id);

    //register Mixpanel SuperProperty
    this.register(mixPanelDataForSuperProperties);

    if (!userData?.roles) {
      this.identify(userData.id);
      mixpanel.people.unset(["isSociabilityMapper", "isSociabilityTeam"]);
      mixpanel.unregister("isSociabilityTeam");
      mixpanel.unregister("isSociabilityMapper");
    }
  }
}

export default AnalyticsService;
